import { apiClient } from "../services/axiosInstance";
import { ServerListResponse, ServerSingleResponse } from "../types";

// TODO: tighten these once the backend response shape stabilises — the index
// signatures are deliberate while the lists API is still evolving.
export type ListItemContext = {
  id?: number;
  name?: string;
  slug?: string;
  image_url?: string;
  image_small_url?: string;
  image_tiny_url?: string;
  pages_viewed?: number;
  total_published_pages?: number;
  total_favorites?: number;
  total_pageviews?: number;
  [key: string]: any;
};

export type ListItem = {
  id?: number;
  list_id?: number;
  context_entity_type_id?: number;
  context_entity_id?: number;
  display_order?: number;
  description?: string;
  comic?: ListItemContext;
  release?: ListItemContext;
  [key: string]: any;
};

export type ListOwner = {
  entity_type: "User" | "Artist";
  id: number;
  name: string;
  url?: string;
  avatar_url?: string;
  avatar_small_url?: string;
  avatar_tiny_url?: string;
};

export type List = {
  entity_type: "List";
  id: number;
  display_name: string;
  description?: string;
  is_public: boolean;
  is_featured?: boolean;
  list_type_id: number;
  slug: string;
  owner_type_id?: number;
  owner_id?: number;
  owner?: ListOwner | null;
  create_time: string;
  update_time: string | null;
  total_items?: number;
  // Aggregate sum of pageviews across the list's items (across the full
  // set). Stamped by the backend on listing endpoints AND on detail.
  total_pageviews?: number;
  // Listing endpoints (My Shelf rail, /v1/lists/, etc.) ship a flat
  // `covers: string[]` (max 3 URLs) instead of `list_items[]`. The rail
  // card only renders three covers — shipping the full release/comic
  // blob per item was wasted bytes. Detail endpoint (`/v1/lists/<id>`)
  // ships `list_items[]` as before; `covers` is absent there.
  covers?: string[];
  list_items?: ListItem[];
  // Public consumer URL (absolute) — backend-populated for user-owned reading lists.
  url?: string;
  // Owner-only edit URL (absolute) — backend-populated for user-owned reading lists.
  edit_url?: string;
  // Custom cover image (populated when a cover has been uploaded)
  image_url?: string;
  image_small_url?: string;
  image_tiny_url?: string;
  // Follow state — stamped on the detail endpoint by handle_entity.
  is_following?: boolean;
  follow_count?: number;
  // Most-mature audience facet across all items in the list. Stamped by
  // handle_entity on /v1/lists/<id>. Maps to the comic audience facet
  // option name ("Adults" / "Teenagers") or "All" when no items carry an
  // audience facet (or the list is empty). Frontend renders via the
  // display-label mapping in ReadingListDetailPane's DetailStats.
  audience_rating?: "All" | "Teenagers" | "Adults";
  // Computed properties for convenience
  name?: string; // Alias for display_name
  list_type?: "comics" | "releases"; // Derived from list_type_id
};

export function postUnfollowList(listId: number) {
  return apiClient.post<ServerSingleResponse<{ success: boolean; follow_count: number }>>(
    `/v1/lists/${listId}/unfollow`,
    { meta: [], payload: {} },
  );
}

export function postFollowList(listId: number) {
  return apiClient.post<ServerSingleResponse<{ success: boolean; follow_count: number }>>(
    `/v1/lists/${listId}/follow`,
    { meta: [], payload: {} },
  );
}

export type ReadingListsShelfFilter = "all" | "own" | "following";
export type ReadingListsShelfSort =
  | "created_time"
  | "update_time"
  | "alphabetical";
export type ReadingListsShelfDirection = "asc" | "desc";

export interface GetReadingListsShelfParams {
  page?: number;
  perPage?: number;
  filter?: ReadingListsShelfFilter;
  q?: string;
  sort?: ReadingListsShelfSort;
  direction?: ReadingListsShelfDirection;
  includeCounts?: boolean;
}

export interface ReadingListsShelfCounts {
  own: number;
  following: number;
  all: number;
}

export function getReadingListsShelf(params: GetReadingListsShelfParams = {}) {
  const search = new URLSearchParams();
  if (params.page != null) search.set("page", String(params.page));
  if (params.perPage != null) search.set("per_page", String(params.perPage));
  if (params.filter) search.set("filter", params.filter);
  if (params.q) search.set("q", params.q);
  if (params.sort) search.set("sort", params.sort);
  if (params.direction) search.set("direction", params.direction);
  if (params.includeCounts) search.set("include_counts", "1");
  const qs = search.toString();
  return apiClient.get<
    ServerListResponse<List, { counts?: ReadingListsShelfCounts }>
  >(qs ? `/v1/lists/?${qs}` : "/v1/lists/");
}

export function postUploadListCoverFile(listId: number, file: File) {
  const data = new FormData();
  data.append("file", file);
  // Don't set Content-Type — axios derives it (with the boundary) from FormData.
  return apiClient.post<
    ServerSingleResponse<{ success: boolean; image_id: number }>
  >(`/v1/lists/${listId}/upload-cover`, data);
}

export function postRemoveListCover(listId: number) {
  return apiClient.post<ServerSingleResponse<{ success: boolean }>>(
    `/v1/lists/${listId}/remove-cover`,
    { meta: [], payload: {} },
  );
}

// Maps to apps/api/content/v1/lists.php → handle_update. Owner-only on the
// backend; the controller silently ignores fields the caller omitted, so it's
// safe to send a partial payload.
export type UpdateListPayload = {
  name?: string;
  description?: string;
  is_public?: boolean;
};

export function postUpdateList(listId: number, payload: UpdateListPayload) {
  return apiClient.post<ServerSingleResponse<List>>(
    `/v1/lists/${listId}/update`,
    { meta: [], payload },
  );
}

// Maps to apps/api/content/v1/lists.php → handle_reorder. Owner-only.
// Send the full new sequence of list_item ids; the backend rewrites
// display_order to match the array index. Backend reads the ids from
// `ordered_item_ids` (not `item_ids`).
export function postReorderListItems(listId: number, itemIds: number[]) {
  return apiClient.post<ServerSingleResponse<{ success: boolean }>>(
    `/v1/lists/${listId}/reorder`,
    { meta: [], payload: { ordered_item_ids: itemIds } },
  );
}

export type CreateListPayload = {
  name: string;
  description?: string;
  is_public?: boolean;
  list_type?: "comics" | "releases";
  // Optional inline-add at creation time — mirrors the `add-to-lists` payload
  // shape. `entity_type` must agree with `list_type` ("comic" ↔ "comics",
  // "release" ↔ "releases"); the backend rejects the mismatch with 400.
  // `list_ids` is the list of ENTITY ids to add (naming is deliberate — keeps
  // parity with /v1/lists/add-to-lists).
  entity_type?: "comic" | "release";
  list_ids?: number[];
};

// /v1/lists/create returns the new List with optional `items_added` and
// `items_failed` stamped when an inline-add was requested.
export type CreateListResults = List & {
  items_added?: number;
  items_failed?: number[];
};

export type AddToListsPayload = {
  list_ids: number[];
};

export type AddToListsResult = {
  list_id: number;
  success: boolean;
  message?: string;
};

export function getComicLists() {
  return apiClient.get<ServerListResponse<List>>("/v1/lists/comics");
}

export function getReleaseLists() {
  return apiClient.get<ServerListResponse<List>>("/v1/lists/releases");
}

export function postCreateComicList(payload: CreateListPayload) {
  return apiClient.post<ServerSingleResponse<CreateListResults>>(
    "/v1/lists/create",
    {
      meta: [],
      payload: { ...payload, list_type: "comics" },
    },
  );
}

export function postCreateReleaseList(payload: CreateListPayload) {
  return apiClient.post<ServerSingleResponse<CreateListResults>>(
    "/v1/lists/create",
    {
      meta: [],
      payload: { ...payload, list_type: "releases" },
    },
  );
}

export function postAddComicToLists(
  comicId: number,
  payload: AddToListsPayload,
) {
  return apiClient.post<ServerSingleResponse<AddToListsResult[]>>(
    `/v1/lists/add-to-lists`,
    { 
      meta: [], 
      payload: { 
        entity_type: "comic", 
        entity_id: comicId, 
        list_ids: payload.list_ids 
      } 
    },
  );
}

export function postAddReleaseToLists(
  releaseId: number,
  payload: AddToListsPayload,
) {
  return apiClient.post<ServerSingleResponse<AddToListsResult[]>>(
    `/v1/lists/add-to-lists`,
    { 
      meta: [], 
      payload: { 
        entity_type: "release", 
        entity_id: releaseId, 
        list_ids: payload.list_ids 
      } 
    },
  );
}

export function postRemoveComicFromList(
  comicId: number,
  listId: number,
) {
  return apiClient.post<ServerSingleResponse<{ success: boolean; message: string }>>(
    `/v1/lists/remove-from-list`,
    { 
      meta: [], 
      payload: { 
        entity_id: comicId, 
        list_id: listId 
      } 
    },
  );
}

export function postRemoveReleaseFromList(
  releaseId: number,
  listId: number,
) {
  return apiClient.post<ServerSingleResponse<{ success: boolean; message: string }>>(
    `/v1/lists/remove-from-list`,
    { 
      meta: [], 
      payload: { 
        entity_id: releaseId, 
        list_id: listId 
      } 
    },
  );
}

export function getList(listId: number) {
  return apiClient.get<ServerSingleResponse<List>>(`/v1/lists/${listId}`);
}

export function postDeleteList(listId: number) {
  return apiClient.post<ServerSingleResponse<{ success: boolean; message: string }>>(
    `/v1/lists/delete`,
    { meta: [], payload: { list_id: listId } },
  );
}
